/**
 * 同一人物の membership 行 (端末リンク) を 1 人にまとめる純粋ヘルパー (R-70、docs/98)。
 *
 * 背景 (2026-09-07 本番 逗子で発覚):
 *   端末の引き継ぎ・復帰をすると、`participants/{uid}/memberships/{districtId}` は **人数分ではなく
 *   端末分** できる (copyMemberships は旧 doc を消さずコピーする)。同一人物を 1 行に畳むために
 *   `membership.linkedFromUids` を使うが、R-70 P6 の書き戻し (`participant/linkBack.ts`) で
 *   このフィールドは **双方向** になった。「他の行の linkedFromUids に出てくる uid は旧端末だから捨てる」
 *   という一方向前提の畳み込みは、双方向リンクだと **グループ全員が捨てられる**。
 *   本番 逗子ではセッション A の参加者 7 人が 1 人と数えられ、配分が事実上できなくなっていた。
 *
 * 正しい畳み込みは「uid ↔ linkedFromUids を無向グラフとみなした **連結成分** = 1 人」。
 * リンクの向き・重複・自己参照・片方向/双方向の混在に依存しない。行の無い uid (別地区でしか
 * membership を持たない旧端末など) も連結の中継点として扱うので、A→X→B のような間接的な
 * つながりも 1 人にまとまる。
 *
 * 代表 uid は **最終アクセスが最新の端末** (lastSeenAt)。配分・集計はこの uid に対して行う。
 * 同着や lastSeenAt 欠落は入力順で決めるため、同じ入力なら常に同じ代表になる (冪等)。
 *
 * 利用側: functions `verification/assignmentEngine.ts` (検証の配分)、
 *   apps/admin `verification-admin/MembershipPicker.tsx` (配分先の手動選択)、
 *   apps/admin `participants-admin` (ユーザー管理の一覧)。
 */
/** 自己参照・空文字・重複を除いたリンク先 uid。 */
function linksOf(row) {
    const out = [];
    for (const id of row.linkedFromUids ?? []) {
        if (typeof id !== 'string' || !id || id === row.uid || out.includes(id))
            continue;
        out.push(id);
    }
    return out;
}
/**
 * membership 行を同一人物 (連結成分) ごとにまとめる。
 *
 * - グループの並びは「そのグループの最初の行が入力に現れた順」(安定)。
 * - 同じ uid の行が複数あった場合は最初の行だけを採用する (collectionGroup では起きないが防御的に)。
 */
export function groupLinkedParticipants(rows) {
    const parent = new Map();
    const ensure = (uid) => {
        if (!parent.has(uid))
            parent.set(uid, uid);
    };
    const find = (uid) => {
        ensure(uid);
        let cur = uid;
        while (parent.get(cur) !== cur) {
            const next = parent.get(cur);
            parent.set(cur, parent.get(next)); // path halving
            cur = parent.get(cur);
        }
        return cur;
    };
    const union = (a, b) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb)
            parent.set(ra, rb);
    };
    const seen = new Set();
    const uniqueRows = [];
    for (const row of rows) {
        if (!row?.uid || seen.has(row.uid))
            continue;
        seen.add(row.uid);
        uniqueRows.push(row);
        ensure(row.uid);
        for (const link of linksOf(row))
            union(row.uid, link);
    }
    const byRoot = new Map();
    const order = [];
    for (const row of uniqueRows) {
        const root = find(row.uid);
        let bucket = byRoot.get(root);
        if (!bucket) {
            bucket = [];
            byRoot.set(root, bucket);
            order.push(root);
        }
        bucket.push(row);
    }
    return order.map((root) => {
        const groupRows = byRoot.get(root);
        // 片方向データ (linkBack 以前) では「他の端末から引き継ぎ元として参照されていない uid」が最終端末。
        // 双方向データでは全員が参照されるので、この手掛かりは効かず lastSeenAt / 入力順で決まる。
        const referenced = new Set();
        for (const row of groupRows)
            for (const link of linksOf(row))
                referenced.add(link);
        const rank = (row) => [
            row.lastSeenAtMs ?? -1,
            referenced.has(row.uid) ? 0 : 1,
        ];
        let primary = groupRows[0];
        for (const row of groupRows) {
            const [aSeen, aFresh] = rank(row);
            const [bSeen, bFresh] = rank(primary);
            // 最終アクセスが新しい方を代表に。同着なら未参照 (= 最終端末) を優先し、なお同着なら入力順。
            if (aSeen > bSeen || (aSeen === bSeen && aFresh > bFresh))
                primary = row;
        }
        return {
            primaryUid: primary.uid,
            primary,
            rows: groupRows,
            uids: [primary.uid, ...groupRows.filter((r) => r.uid !== primary.uid).map((r) => r.uid)],
        };
    });
}
/**
 * 「この uid は誰か」の逆引き map (uid → 代表 uid)。既存データ (旧 uid で保存済みの選択・割当) を
 * 人物単位に読み替えるために使う。
 */
export function buildPrimaryUidMap(groups) {
    const map = new Map();
    for (const g of groups)
        for (const uid of g.uids)
            map.set(uid, g.primaryUid);
    return map;
}
/**
 * uid → 同一人物ID (代表端末 uid) の対応表 (2026-09-18)。
 *
 * エクスポート (投稿・参加者・アンケート・検証・気づき/処方箋) に**人単位の集計軸**を入れるための
 * 共通処理。どの出力でも同じ値になるよう、入力順に依存しないよう uid 順に整えてから畳み込む
 * (`groupLinkedParticipants` は代表の同着時に入力順で決めるため、呼出側の並び順の差が出ないようにする)。
 */
export function buildPersonIdMap(rows) {
    const sorted = [...rows].sort((a, b) => (a.uid < b.uid ? -1 : a.uid > b.uid ? 1 : 0));
    const map = new Map();
    for (const g of groupLinkedParticipants(sorted)) {
        for (const row of g.rows) {
            map.set(row.uid, {
                personId: g.primaryUid,
                deviceCount: g.rows.length,
                isPrimary: row.uid === g.primaryUid,
            });
        }
    }
    return map;
}
//# sourceMappingURL=linkedParticipants.js.map